"use client";
import { Slider } from "@/components/ui/slider";
import BillReceipt from "../billReceipt/billReceipt.component";
import WhatsAppPhone from "../whatsAppPhone/whatsAppPhone.component";

import { cn } from "@/lib/utils";
import type { CounterDemoData } from "./counterDemo.type";
import { useCounterDemo } from "./hooks";

const panelClass =
  "grid content-start gap-4 rounded-[30px] bg-white p-6 text-ink sm:p-7";

// The preview's `counter_demo`: pick a customer and drag the bill; the
// profile, receipt, wallet and the customer's WhatsApp message update live.
const CounterDemo = ({
  ariaLabel,
  customerStep,
  customers,
  billStep,
  billLabel,
  billAriaLabel,
  bill,
  fine,
  rule,
  walletLabel,
  meta,
  newMeta,
  receipt,
  chat,
}: CounterDemoData) => {
  const demo = useCounterDemo({
    customers,
    initialBill: bill.initial,
    rule,
    meta,
    newMeta,
    noReward: receipt.noReward,
    message: chat.message,
    savedSuffix: chat.savedSuffix,
  });

  return (
    <figure
      role="group"
      aria-label={ariaLabel}
      className="m-0 grid items-start gap-7 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]"
    >
      <div className={panelClass}>
        <h3 className="text-base font-bold">{customerStep}</h3>
        <div className="grid gap-2.5 sm:grid-cols-3">
          {customers.map((customer) => {
            const isOn = customer.key === demo.customer.key;
            return (
              <button
                key={customer.key}
                type="button"
                aria-pressed={isOn}
                onClick={() => demo.selectCustomer(customer.key)}
                className={cn(
                  "grid cursor-pointer justify-items-center gap-1 rounded-[18px] border-2 bg-white px-2 py-3.5 transition-colors duration-200 focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none",
                  isOn
                    ? "border-primary bg-tone-blush"
                    : "border-line hover:border-line-warm",
                )}
              >
                <span
                  aria-hidden="true"
                  className="grid size-[38px] place-items-center rounded-full bg-primary font-extrabold text-white"
                >
                  {customer.name.charAt(0)}
                </span>
                <b className="text-[15px]">{customer.name}</b>
                <small className="text-xs text-ink-muted">
                  {customer.segment}
                </small>
              </button>
            );
          })}
        </div>
        <h3 className="text-base font-bold">{billStep}</h3>
        <label className="grid gap-2 text-[15px] font-semibold">
          <span>
            {billLabel}
            <output className="ml-1.5 font-extrabold text-primary">
              {demo.billText}
            </output>
          </span>
          <Slider
            min={bill.min}
            max={bill.max}
            step={bill.step}
            value={[demo.bill]}
            aria-label={billAriaLabel}
            onValueChange={([next]) => demo.setBill(next)}
          />
        </label>
        <p className="text-sm text-ink-muted">{fine}</p>
      </div>

      <div aria-live="polite" className={panelClass}>
        <div className="grid grid-cols-[auto_1fr] items-center gap-3.5 sm:grid-cols-[auto_1fr_auto]">
          <span
            aria-hidden="true"
            className="grid size-[38px] place-items-center rounded-full bg-primary font-extrabold text-white"
          >
            {demo.customer.name.charAt(0)}
          </span>
          <div className="grid gap-[3px]">
            <b className="text-base">{demo.customer.name}</b>
            <span className="justify-self-start rounded-full bg-tone-mint px-3 py-[3px] text-[12.5px] font-bold text-tone-mint-ink">
              {demo.customer.segment}
            </span>
            <small className="text-[13px] text-ink-muted">
              {demo.metaText}
            </small>
          </div>
          <div className="col-span-full grid sm:col-span-1 sm:text-right">
            <small className="text-[13px] text-ink-muted">{walletLabel}</small>
            <b className="text-2xl">{demo.walletText}</b>
          </div>
        </div>
        <BillReceipt
          title={receipt.bill}
          rows={[
            { label: receipt.bill, value: demo.billText },
            { label: receipt.reward, value: demo.discountText, tone: "minus" },
          ]}
          total={{ label: receipt.pay, value: demo.payText }}
          extra={{
            label: receipt.cashback,
            value: demo.earnText,
            tone: "earn",
          }}
        />
        <WhatsAppPhone
          chat={{
            initial: chat.shopInitial,
            shop: chat.shop,
            subtitle: chat.subtitle,
          }}
          messages={[{ content: demo.chatText, time: chat.time }]}
        />
      </div>
    </figure>
  );
};

export default CounterDemo;
